"use client";

import { localPointFromSvg } from "./scatter-svg";
import type { scaleLinear } from "@visx/scale";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import type { LineConfig, Margin, TooltipData } from "./chart-context";
import { useScheduledTooltip } from "./use-scheduled-tooltip";
import { normalizeYAxisId } from "./y-axis-scales";
import {
  type AnyXScale,
  isBandScale,
  type XRawValue,
  type XScaleKind,
} from "./x-scale";

type ScaleLinear = ReturnType<typeof scaleLinear<number>>;

export interface ChartSelection {
  startX: number;
  endX: number;
  startIndex: number;
  endIndex: number;
  active: boolean;
}

interface UseChartInteractionParams {
  xScale: AnyXScale;
  xScaleKind: XScaleKind;
  yScale: ScaleLinear;
  yScales: Record<string, ScaleLinear>;
  data: Record<string, unknown>[];
  lines: LineConfig[];
  margin: Margin;
  xAccessor: (d: Record<string, unknown>) => XRawValue;
  bisectX:
    | ((data: Record<string, unknown>[], target: number, lo: number) => number)
    | null;
  canInteract: boolean;
}

interface ChartInteractionResult {
  tooltipData: TooltipData | null;
  setTooltipData: React.Dispatch<React.SetStateAction<TooltipData | null>>;
  clearTooltip: () => void;
  selection: ChartSelection | null;
  clearSelection: () => void;
  interactionHandlers: {
    onMouseMove?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseLeave?: () => void;
    onMouseDown?: (event: React.MouseEvent<SVGGElement>) => void;
    onMouseUp?: () => void;
    onTouchStart?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchMove?: (event: React.TouchEvent<SVGGElement>) => void;
    onTouchEnd?: () => void;
    onTouchCancel?: () => void;
  };
  interactionStyle: React.CSSProperties;
}

export function useChartInteraction({
  xScale,
  xScaleKind,
  yScale,
  yScales,
  data,
  lines,
  margin,
  xAccessor,
  bisectX,
  canInteract,
}: UseChartInteractionParams): ChartInteractionResult {
  const [selection, setSelection] = useState<ChartSelection | null>(null);
  const hoverCapable = useHoverCapable();
  const {
    tooltipData,
    setTooltipData,
    scheduleTooltip,
    clearTooltip,
    resetTooltipDedupe,
  } = useScheduledTooltip<TooltipData>();

  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number>(0);
  const lastHoveredXRef = useRef<number | null>(null);
  const lastTappedIndexRef = useRef<number | null>(null);
  const touchTooltipVisibleRef = useRef(false);
  const containerElRef = useRef<SVGGElement | null>(null);

  const resolveBandIndex = useCallback(
    (pixelX: number): number | null => {
      if (!isBandScale(xScale)) return null;
      const step = xScale.step();
      if (step <= 0) return null;
      const [rangeStart, rangeEnd] = xScale.range();
      const clamped = Math.max(rangeStart, Math.min(rangeEnd, pixelX));
      const domain = xScale.domain();
      const raw = Math.floor((clamped - rangeStart) / step);
      return Math.max(0, Math.min(domain.length - 1, raw));
    },
    [xScale],
  );

  const bandCenter = useCallback(
    (index: number): number => {
      if (!isBandScale(xScale)) return 0;
      const domain = xScale.domain();
      const key = domain[index];
      if (key == null) return 0;
      const pos = xScale(key) ?? 0;
      return pos + xScale.bandwidth() / 2;
    },
    [xScale],
  );

  const resolveTooltipFromX = useCallback(
    (pixelX: number): TooltipData | null => {
      if (xScaleKind === "band") {
        const index = resolveBandIndex(pixelX);
        if (index == null) return null;
        const d = data[index];
        if (!d) return null;
        const yPositions: Record<string, number> = {};
        for (const line of lines) {
          const value = d[line.dataKey];
          if (typeof value === "number") {
            const axisScale = yScales[normalizeYAxisId(line.yAxisId)] ?? yScale;
            yPositions[line.dataKey] = axisScale(value) ?? 0;
          }
        }
        return {
          point: d,
          index,
          x: bandCenter(index),
          yPositions,
        };
      }

      if (!bisectX || isBandScale(xScale)) return null;
      const invertedRaw = xScale.invert(pixelX);
      const x0 = Number(invertedRaw);
      const index = bisectX(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];

      if (!d0) {
        return null;
      }

      let d = d0;
      let finalIndex = index - 1;
      if (d1) {
        const d0Time = Number(xAccessor(d0));
        const d1Time = Number(xAccessor(d1));
        if (x0 - d0Time > d1Time - x0) {
          d = d1;
          finalIndex = index;
        }
      }

      const yPositions: Record<string, number> = {};
      for (const line of lines) {
        const value = d[line.dataKey];
        if (typeof value === "number") {
          const axisScale = yScales[normalizeYAxisId(line.yAxisId)] ?? yScale;
          yPositions[line.dataKey] = axisScale(value) ?? 0;
        }
      }

      const xRaw = xAccessor(d);
      const xPos = isBandScale(xScale)
        ? 0
        : (xScale(xRaw as never) ?? 0);

      return {
        point: d,
        index: finalIndex,
        x: xPos,
        yPositions,
      };
    },
    [
      xScale,
      xScaleKind,
      yScale,
      yScales,
      data,
      lines,
      xAccessor,
      bisectX,
      resolveBandIndex,
      bandCenter,
    ],
  );

  const resolveIndexFromX = useCallback(
    (pixelX: number): number => {
      if (xScaleKind === "band") {
        return resolveBandIndex(pixelX) ?? 0;
      }
      if (!bisectX || isBandScale(xScale)) return 0;
      const invertedRaw = xScale.invert(pixelX);
      const x0 = Number(invertedRaw);
      const index = bisectX(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];
      if (!d0) {
        return 0;
      }
      if (d1) {
        const d0Time = Number(xAccessor(d0));
        const d1Time = Number(xAccessor(d1));
        if (x0 - d0Time > d1Time - x0) {
          return index;
        }
      }
      return index - 1;
    },
    [xScale, xScaleKind, data, xAccessor, bisectX, resolveBandIndex],
  );

  const getChartX = useCallback(
    (
      event: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>,
      touchIndex = 0
    ): number | null => {
      let point: { x: number; y: number } | null = null;

      if ("touches" in event) {
        const touch = event.touches[touchIndex];
        if (!touch) {
          return null;
        }
        const svg = event.currentTarget.ownerSVGElement;
        if (!svg) {
          return null;
        }
        point = localPointFromSvg(svg, touch.clientX, touch.clientY);
      } else {
        point = localPointFromSvg(
          event.currentTarget.ownerSVGElement,
          event.clientX,
          event.clientY
        );
      }

      if (!point) {
        return null;
      }
      return point.x - margin.left;
    },
    [margin.left]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGGElement>) => {
      const chartX = getChartX(event);
      if (chartX === null) {
        return;
      }

      if (isDraggingRef.current) {
        const startX = Math.min(dragStartXRef.current, chartX);
        const endX = Math.max(dragStartXRef.current, chartX);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
        return;
      }

      lastHoveredXRef.current = chartX;
      const tooltip = resolveTooltipFromX(chartX);
      if (tooltip) {
        scheduleTooltip(tooltip);
      }
    },
    [getChartX, resolveTooltipFromX, resolveIndexFromX, scheduleTooltip]
  );

  const handleMouseLeave = useCallback(() => {
    lastHoveredXRef.current = null;
    clearTooltip();
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, [clearTooltip]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<SVGGElement>) => {
      const chartX = getChartX(event);
      if (chartX === null) {
        return;
      }
      isDraggingRef.current = true;
      dragStartXRef.current = chartX;
      clearTooltip();
      setSelection(null);
    },
    [getChartX, clearTooltip]
  );

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
    }
    setSelection(null);
  }, []);

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<SVGGElement>) => {
      containerElRef.current = event.currentTarget;
      if (event.touches.length === 1) {
        const chartX = getChartX(event, 0);
        if (chartX === null) {
          return;
        }
        lastHoveredXRef.current = chartX;
        const tooltip = resolveTooltipFromX(chartX);
        if (tooltip) {
          if (
            touchTooltipVisibleRef.current &&
            lastTappedIndexRef.current === tooltip.index
          ) {
            clearTooltip();
            touchTooltipVisibleRef.current = false;
            lastTappedIndexRef.current = null;
            return;
          }
          scheduleTooltip(tooltip);
          touchTooltipVisibleRef.current = true;
          lastTappedIndexRef.current = tooltip.index;
        }
      } else if (event.touches.length === 2) {
        resetTooltipDedupe();
        clearTooltip();
        touchTooltipVisibleRef.current = false;
        lastTappedIndexRef.current = null;
        const x0 = getChartX(event, 0);
        const x1 = getChartX(event, 1);
        if (x0 === null || x1 === null) {
          return;
        }
        const startX = Math.min(x0, x1);
        const endX = Math.max(x0, x1);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
      }
    },
    [
      getChartX,
      resolveTooltipFromX,
      resolveIndexFromX,
      scheduleTooltip,
      resetTooltipDedupe,
      clearTooltip,
    ]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<SVGGElement>) => {
      if (event.touches.length === 2) {
        const x0 = getChartX(event, 0);
        const x1 = getChartX(event, 1);
        if (x0 === null || x1 === null) {
          return;
        }
        const startX = Math.min(x0, x1);
        const endX = Math.max(x0, x1);
        setSelection({
          startX,
          endX,
          startIndex: resolveIndexFromX(startX),
          endIndex: resolveIndexFromX(endX),
          active: true,
        });
      }
    },
    [getChartX, resolveIndexFromX]
  );

  const handleTouchEnd = useCallback(() => {
    setSelection(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  // Re-anchor tooltip/crosshair when x-scale or visible data changes (e.g. brush zoom commit).
  useEffect(() => {
    if (!canInteract || lastHoveredXRef.current === null) {
      return;
    }
    const tooltip = resolveTooltipFromX(lastHoveredXRef.current);
    if (tooltip) {
      scheduleTooltip(tooltip, `${tooltip.index}:${Math.round(tooltip.x)}`);
      return;
    }
    clearTooltip();
  }, [canInteract, clearTooltip, resolveTooltipFromX, scheduleTooltip]);

  useEffect(() => {
    if (!canInteract) return;
    const onDocPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      if (!touchTooltipVisibleRef.current) return;
      const container = containerElRef.current;
      if (!container) return;
      const target = event.target as Node | null;
      if (target && container.contains(target)) return;
      clearTooltip();
      touchTooltipVisibleRef.current = false;
      lastTappedIndexRef.current = null;
    };
    document.addEventListener("pointerdown", onDocPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown, true);
    };
  }, [canInteract, clearTooltip]);

  const interactionHandlers = canInteract
    ? {
        ...(hoverCapable
          ? {
              onMouseMove: handleMouseMove,
              onMouseLeave: handleMouseLeave,
              onMouseDown: handleMouseDown,
              onMouseUp: handleMouseUp,
            }
          : {}),
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        // A two-finger drag now starts a browser pinch, which ends the sequence
        // with touchcancel instead of touchend. Without this the range
        // selection stays on screen with active: true.
        onTouchCancel: handleTouchEnd,
      }
    : {};

  const interactionStyle: React.CSSProperties = {
    cursor: canInteract ? "crosshair" : "default",
    touchAction: "manipulation",
  };

  return {
    tooltipData,
    setTooltipData,
    clearTooltip,
    selection,
    clearSelection,
    interactionHandlers,
    interactionStyle,
  };
}
